import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import MemberBanner from "../componenets/MemberBanner";
import { formatNumber, Duration, asRows } from "../lib/leaderboard";
import { RESULTS_ENABLED } from "../lib/features";
import { readMetricsSnapshot, triggerRefresh } from "../lib/metricsCache";

const TrackIndex = () => {
    const [tracks, setTracks] = useState([]);
    const [postCounts, setPostCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const [trackSnap, mediaSnap] = await Promise.all([
                    getDocs(collection(db, "tracks")),
                    getDocs(collection(db, "media")),
                ]);
                if (cancelled) return;
                const trackList = trackSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                const counts = {};
                mediaSnap.docs.forEach((d) => {
                    const data = d.data() || {};
                    if (data.archived) return;
                    const slug = data.track;
                    if (typeof slug === "string") counts[slug] = (counts[slug] || 0) + 1;
                });
                setTracks(trackList);
                setPostCounts(counts);
            } catch (err) {
                console.error("Failed to load tracks", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const snapshot = await readMetricsSnapshot();
                if (cancelled) return;
                if (snapshot && snapshot.payload) {
                    setMetrics(snapshot.payload);
                    if (snapshot.isStale) {
                        const fresh = await triggerRefresh();
                        if (!cancelled && fresh) setMetrics(fresh);
                    }
                    return;
                }
                const fresh = await triggerRefresh();
                if (!cancelled && fresh) setMetrics(fresh);
            } catch (err) {
                console.warn("Failed to load metrics", err);
            }
        };
        run();
        return () => { cancelled = true; };
    }, []);

    const orderedTracks = useMemo(() => {
        return tracks
            .filter((t) => t.active !== false)
            .sort((a, b) => (a.position || 0) - (b.position || 0));
    }, [tracks]);

    const shareCombos = useMemo(() => {
        const combos = asRows(metrics && metrics.metrics && metrics.metrics.combos);
        return combos.filter((c) => c.source === "ambasada-share");
    }, [metrics]);
    const totalShareViews = useMemo(
        () => shareCombos.reduce((s, c) => s + (c.y || 0), 0),
        [shareCombos]
    );
    const totalShareTime = (metrics && metrics.shareStats && metrics.shareStats.totaltime) || 0;
    const showHero = metrics && (totalShareViews > 0 || totalShareTime > 0);

    return (
        <div className="bg-brand-bg min-h-screen">
            <header className="bg-white border-b border-brand-panel">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12 lg:py-14">
                    <h1 className="flex flex-wrap items-center gap-x-4 sm:gap-x-5 gap-y-2 font-brand text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink leading-[1.05]">
                        <Link
                            to="/"
                            aria-label="Home"
                            className="inline-flex items-center"
                        >
                            <img
                                src="/ambasada-mark.svg"
                                alt="Ambasada"
                                width="40"
                                height="40"
                                className="h-12 sm:h-14 lg:h-16 w-auto"
                            />
                        </Link>
                        <span>community<span className="text-brand-red">.</span></span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-brand-muted max-w-xl">
                        Each track has its own posts. Share whichever fits the community you're reaching.
                    </p>
                    <div className="mt-5 h-1 w-24 bg-brand-red rounded-full" />
                </div>
            </header>

            <MemberBanner />

            <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
                {showHero && (
                    <section className="relative rounded-3xl bg-white border border-brand-panel shadow-brand overflow-hidden mb-10 sm:mb-14">
                        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-brand-red/5 pointer-events-none" aria-hidden="true" />
                        <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-brand-red/[0.03] pointer-events-none" aria-hidden="true" />
                        <div className="relative p-8 sm:p-12 lg:p-14">
                            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-red">
                                Community share metrics
                            </div>
                            <div className="mt-8 flex flex-col sm:flex-row sm:items-stretch gap-6 sm:gap-0">
                                <div className="flex-1 min-w-0">
                                    <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                        {formatNumber(totalShareViews)}
                                    </div>
                                    <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                        {totalShareViews === 1 ? "click" : "clicks"} delivered
                                    </div>
                                </div>
                                {totalShareTime > 0 && (
                                    <>
                                        <div className="hidden sm:block w-px self-stretch bg-brand-panel mx-6 lg:mx-10" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                <Duration seconds={totalShareTime} />
                                            </div>
                                            <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                of prime time
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {loading && <div className="text-brand-muted">Loading tracks...</div>}

                {!loading && orderedTracks.length === 0 && (
                    <div className="rounded-2xl bg-white border border-brand-panel p-8 text-sm text-brand-muted">
                        No tracks configured yet.
                    </div>
                )}

                {!loading && orderedTracks.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                        {orderedTracks.map((track) => {
                            const count = postCounts[track.slug] || 0;
                            return (
                                <Link
                                    key={track.id}
                                    to={`/t/${track.slug}`}
                                    className="group block rounded-3xl bg-white border border-brand-panel shadow-brand overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg"
                                >
                                    <div className="p-8 sm:p-10">
                                        <div className="font-brand font-bold text-brand-ink text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight">
                                            {track.name}<span className="text-brand-red">.</span>
                                        </div>
                                        {track.subhead && (
                                            <p className="mt-4 text-sm sm:text-base text-brand-muted max-w-md">
                                                {track.subhead}
                                            </p>
                                        )}
                                        <div className="mt-6 flex items-center justify-between">
                                            <div className="text-sm font-medium text-brand-ink">
                                                {count === 0 ? (
                                                    <span className="text-brand-muted">No posts yet</span>
                                                ) : (
                                                    <>{count} {count === 1 ? "post" : "posts"} to share</>
                                                )}
                                            </div>
                                            <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red group-hover:gap-2.5 transition-all">
                                                Open feed
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}

                {RESULTS_ENABLED && (
                    <section className="mt-12 flex items-center justify-end text-sm text-brand-muted">
                        <Link to="/results" className="font-medium text-brand-ink hover:text-brand-red transition-colors">
                            Global results →
                        </Link>
                    </section>
                )}
            </main>
        </div>
    );
};

export default TrackIndex;
